import React from 'react';

type UpfrontPricingType = {
	HasData: boolean;
	HasMemberData: boolean;
	MemberUpfrontPaymentAmount?: number | null;
	UpfrontPaymentAmount?: number | null;
	UpfrontPaymentPercentage?: number | null;
};

type FinanceInstallmentProps = {
	UpfrontPricing: UpfrontPricingType;
	UseUpfrontPricingAmounts: boolean;
	DeliveryPricingInPricePod?: boolean;
	NewDayFinanceQuote: any;
	isSwitch24Product: boolean;
	switchMonthlyPayment: number;
	useEnhancedFinanceProposition?: boolean;
	isFinancePromotion?: boolean;
};

export function FinanceInstallments({
	UpfrontPricing = {
		HasData: false,
		HasMemberData: false,
	},
	UseUpfrontPricingAmounts,
	DeliveryPricingInPricePod,
	NewDayFinanceQuote,
	isSwitch24Product,
	switchMonthlyPayment,
	useEnhancedFinanceProposition,
	isFinancePromotion,
}: FinanceInstallmentProps) {
	const monthlyPrice = isSwitch24Product
		? `£${switchMonthlyPayment?.toFixed(2)}`
		: NewDayFinanceQuote.FormattedMonthlyPriceString;

	const upfrontCriteriaMet = UseUpfrontPricingAmounts && UpfrontPricing.HasData;

	const correctUpfrontValue = isSwitch24Product
		? UpfrontPricing?.MemberUpfrontPaymentAmount || UpfrontPricing?.UpfrontPaymentAmount
		: UpfrontPricing?.UpfrontPaymentAmount;

	if (useEnhancedFinanceProposition) {
		const upfrontText = upfrontCriteriaMet
			? `& £${correctUpfrontValue?.toFixed(2)} upfront today`
			: '';

		return (
			<div data-testid="finance-text-with-optional-upfront">
				{isFinancePromotion && !isSwitch24Product ? (
					<span className="text-body-sm">{`or from ${monthlyPrice} monthly*`}</span>
				) : (
					<>
						<div>
							<span className="text-body-sm">
								<span>From </span>
								<span className="text-lg text-title-sm">{monthlyPrice}</span>
								<span className="text-body-sm text-title-sm"> per month*</span>
							</span>
						</div>
						{upfrontText && (
							<span className="text-body-sm" data-testid="finance-upfront-text">
								{upfrontText}
							</span>
						)}
					</>
				)}
			</div>
		);
	}

	let financeText = DeliveryPricingInPricePod
		? `from ${monthlyPrice} monthly*`
		: `or from ${monthlyPrice} monthly*`;

	if (upfrontCriteriaMet) {
		const formattedUpfront = correctUpfrontValue?.toFixed(2);
		financeText = isSwitch24Product
			? `from ${monthlyPrice} monthly*\nplus £${formattedUpfront} upfront`
			: `from ${monthlyPrice} monthly* plus\n£${formattedUpfront} upfront today*`;
	}

	return (
		<div
			className={upfrontCriteriaMet ? 'whitespace-pre-line leading-normal' : ''}
			data-testid="finance-text-with-optional-upfront">
			{financeText}
			<span
				className={isSwitch24Product ? '' : 'ico ico-chevron-right ico-lg'}
				data-testid="optional-chevron-element"
			/>
		</div>
	);
}

FinanceInstallments.defaultProps = {
	useEnhancedFinanceProposition: false,
	isFinancePromotion: false,
	DeliveryPricingInPricePod: true,
};
